import React, { useState } from 'react';
import { shotSrcSet } from './shots';
import { BrandButton, Highlight, Kicker, Paragraphs, Squiggle, assetUrl } from './ui';

/*
 * Figma "Content" (node 35713:340): intro heading + four dashed cards, 60px apart.
 * Card 1600×937, text column 662px starting 64px in, screenshot 859×632 that
 * bleeds 41px (right) / 50px (left) past the card edge.
 */

const CARD_BG = ['bg-white', 'bg-brand-mint', 'bg-white', 'bg-brand-mint'];

export const FEATURE_ICONS = ['chart', 'globe', 'coin', 'bolt', 'team'];

const iconSrc = (item, i) => `/brand/icon-${FEATURE_ICONS.includes(item.icon) ? item.icon : FEATURE_ICONS[i % FEATURE_ICONS.length]}.svg`;

const Card = ({ index, children, testId, id }) => (
  <div
    id={id}
    data-testid={testId}
    className={`hp-card relative flex flex-col gap-10 px-5 py-8 sm:px-8 sm:py-10 lg:min-h-[min(937px,52.0556vw)] lg:flex-row lg:items-center lg:gap-0 lg:px-0 lg:py-[clamp(40px,6.1667vw,111px)] ${CARD_BG[index % 4]}`}
  >
    {children}
  </div>
);

const TextColumn = ({ block, onAuth, className = '' }) => (
  <div className={className}>
    <Kicker className="text-brand-ink">{block.kicker}</Kicker>
    <h3 className="hp-h2 mt-[clamp(16px,1.3333vw,24px)] text-[#253746]">
      <Highlight text={block.title} />
    </h3>
    <div className="hp-body mt-[clamp(16px,1.3333vw,24px)] text-[#253746]">
      <Paragraphs text={block.body} />
    </div>
    {block.cta_label && (
      <BrandButton to={block.cta_link} onAuth={onAuth} className="mt-[clamp(24px,1.7778vw,32px)]">
        {block.cta_label}
      </BrandButton>
    )}
  </div>
);

const Screenshot = ({ src, alt, className = '' }) => (
  <div className={`hp-shot relative aspect-[859/632] ${className}`}>
    <img src={assetUrl(src)} srcSet={shotSrcSet(src)} sizes="(min-width: 1024px) 55vw, 100vw" alt={alt} loading="lazy" decoding="async" className="absolute left-0 top-0 h-full w-auto max-w-none" />
  </div>
);

const ImageBlock = ({ block, index, onAuth }) => {
  const imageLeft = index % 2 === 1;
  return (
    <Card index={index} id={`blok-${index + 1}`} testId={`content-block-${index + 1}`}>
      <TextColumn
        block={block}
        onAuth={onAuth}
        className={
          imageLeft
            ? 'lg:order-2 lg:ml-[4.0625%] lg:w-[41.375%] lg:flex-shrink-0'
            : 'lg:ml-[4%] lg:w-[41.375%] lg:flex-shrink-0'
        }
      />
      <Screenshot
        src={block.image_url}
        alt={block.kicker}
        className={
          imageLeft
            ? 'lg:order-1 lg:-ml-[3.125%] lg:w-[53.6875%] lg:flex-shrink-0'
            : 'lg:ml-[3.5%] lg:-mr-[2.5625%] lg:w-[53.6875%] lg:flex-shrink-0'
        }
      />
    </Card>
  );
};

const FeatureList = ({ items }) => {
  const [active, setActive] = useState(0);
  return (
    <ul className="w-full" data-testid="feature-list">
      {items.map((item, i) => {
        const open = i === active;
        return (
          <li
            key={i}
            className={`transition-shadow duration-300 ${
              open
                ? 'rounded-[20px] bg-white shadow-[0_80px_30px_rgba(0,0,0,0.06),0_40px_16px_rgba(0,0,0,0.05),0_10px_8px_rgba(0,0,0,0.08),0_10px_4px_rgba(0,0,0,0.03),0_2px_2px_rgba(0,0,0,0.02)]'
                : 'border-b border-brand-line'
            }`}
            data-testid={`feature-card-${i}`}
          >
            <button
              type="button"
              onClick={() => setActive(i)}
              aria-expanded={open}
              className={`flex w-full items-center gap-[clamp(14px,1.4444vw,26px)] px-[clamp(18px,2vw,36px)] text-left ${
                open ? 'pt-[clamp(22px,2vw,36px)]' : 'h-[clamp(76px,6.3889vw,115px)]'
              }`}
            >
              <span
                aria-hidden
                className={`h-[clamp(24px,1.6667vw,30px)] w-[clamp(24px,1.6667vw,30px)] flex-shrink-0 ${open ? 'bg-brand' : 'bg-[#253746]'}`}
                style={{
                  WebkitMask: `url(${iconSrc(item, i)}) center / contain no-repeat`,
                  mask: `url(${iconSrc(item, i)}) center / contain no-repeat`,
                }}
              />
              <span
                className={`flex-1 text-[clamp(18px,1.3889vw,25px)] font-semibold leading-[26px] ${
                  open ? 'text-brand-ink' : 'text-[#253746]'
                }`}
              >
                {item.title}
              </span>
              <span
                className={`text-[clamp(24px,1.9444vw,35px)] font-bold leading-[26px] ${
                  open ? 'text-brand-ink' : 'text-[#253746]'
                }`}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-300 ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <p
                className={`overflow-hidden pl-[calc(clamp(18px,2vw,36px)+clamp(24px,1.6667vw,30px)+clamp(14px,1.4444vw,26px))] pr-[clamp(18px,3.3333vw,60px)] font-['Inter',sans-serif] text-[clamp(15px,1vw,18px)] leading-[1.6667] text-[#253746] ${
                  open ? 'pb-[clamp(20px,1.8333vw,33px)] pt-[clamp(8px,0.6667vw,12px)]' : ''
                }`}
              >
                {item.description}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const FeaturesBlock = ({ features, onAuth }) => (
  <Card index={3} id="functies" testId="content-block-4">
    <div className="order-2 lg:order-1 lg:ml-[4%] lg:w-[36.875%] lg:flex-shrink-0">
      <FeatureList items={features.items} />
    </div>
    <TextColumn
      block={features}
      onAuth={onAuth}
      className="order-1 lg:order-2 lg:ml-[13.0625%] lg:w-[42.6875%] lg:flex-shrink-0"
    />
  </Card>
);

const ContentSection = ({ intro, blocks, features, onAuth }) => (
  <section id="blokken" className="hp-container" data-testid="content-section">
    <Squiggle className="mt-[clamp(28px,2.5vw,45px)]" />
    <h2 className="hp-h2-outfit mt-[clamp(28px,3.2222vw,58px)] text-center text-[#243745]">
      <Highlight text={intro.title} />
    </h2>
    <div className="mt-[clamp(24px,2.6111vw,47px)] flex flex-col gap-[clamp(24px,3.3333vw,60px)]">
      {blocks.map((block, i) => (
        <ImageBlock key={i} block={block} index={i} onAuth={onAuth} />
      ))}
      <FeaturesBlock features={features} onAuth={onAuth} />
    </div>
  </section>
);

export default ContentSection;
